import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import LandingLanguageSwitcher from './LandingLanguageSwitcher';
import { getPracticeSiteUrl } from './practiceSiteUrl';
import { useLandingLocale } from './useLandingLocale';
import '../../../public/css/landing.css';

export default function StudentParentLandingPage(): JSX.Element {
  const practiceUrl = useMemo(() => getPracticeSiteUrl(), []);
  const { locale, setLocale, copy: t } = useLandingLocale();

  return (
    <>
      <nav className="nav">
        <div className="wrap nav-in">
          <Link className="brand" to="/">
            <span className="logo">G</span>
            <span>Giỏi Tiếng Anh</span>
          </Link>
          <div className="nav-links">
            <a href="#practice">{t.navPractice}</a>
            <a href="#parents">{t.navParents}</a>
            <a href="#faq">{t.navFaq}</a>
          </div>
          <div className="nav-actions">
            <LandingLanguageSwitcher
              locale={locale}
              label={t.switchLanguage}
              langVi={t.langVi}
              langEn={t.langEn}
              onChange={setLocale}
            />
            <Link className="btn soft" to="/login">{t.login}</Link>
            <a className="btn primary" href={practiceUrl}>{t.tryFree}</a>
          </div>
        </div>
      </nav>

      <main>
        <section className="hero">
          <div className="wrap hero-grid">
            <div>
              <div className="eyebrow">{t.heroEyebrow}</div>
              <h1>
                {t.heroTitleBefore}
                <span className="grad">{t.heroTitleHighlight}</span>
              </h1>
              <p className="lead">{t.heroLead}</p>
              <div className="hero-actions">
                <a className="btn primary large" href={practiceUrl}>{t.tryFreeLong}</a>
                <Link className="btn large" to="/login">{t.login}</Link>
                <a className="btn large" href="#parents">{t.imParent}</a>
              </div>
              <div className="trust-line">
                <span>{t.trust1}</span>
                <span>{t.trust2}</span>
                <span>{t.trust3}</span>
              </div>
            </div>
            <div className="illus" aria-hidden="true">
              <div className="screen">
                <h3>{t.mockTitle}</h3>
                <p>{t.mockLead}</p>
                <div className="pill-row">
                  <span className="pill">{t.pillSpeaking}</span>
                  <span className="pill">{t.pillListening}</span>
                  <span className="pill">{t.pillRoleplay}</span>
                  <span className="pill">{t.pillWriting}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="practice" className="block">
          <div className="wrap">
            <div className="section-head">
              <div className="kicker">{t.practiceKicker}</div>
              <h2>{t.practiceTitle}</h2>
              <p>{t.practiceLead}</p>
            </div>
            <div className="grid3">
              <article className="card">
                <div className="icon">⚡</div>
                <h3>{t.card1Title}</h3>
                <p>{t.card1Body}</p>
              </article>
              <article className="card">
                <div className="icon">↻</div>
                <h3>{t.card2Title}</h3>
                <p>{t.card2Body}</p>
              </article>
              <article className="card">
                <div className="icon">◎</div>
                <h3>{t.card3Title}</h3>
                <p>{t.card3Body}</p>
              </article>
            </div>
          </div>
        </section>

        <section id="parents" className="block">
          <div className="wrap">
            <div className="parent-box">
              <div className="kicker">{t.parentsKicker}</div>
              <h3>{t.parentsTitle}</h3>
              <p>{t.parentsLead}</p>
              <div className="bullets">
                <div className="bullet">
                  <div className="check">✓</div>
                  <div>
                    <b>{t.parentsB1Title}</b>
                    <span>{t.parentsB1Body}</span>
                  </div>
                </div>
                <div className="bullet">
                  <div className="check">✓</div>
                  <div>
                    <b>{t.parentsB2Title}</b>
                    <span>{t.parentsB2Body}</span>
                  </div>
                </div>
                <div className="bullet">
                  <div className="check">✓</div>
                  <div>
                    <b>{t.parentsB3Title}</b>
                    <span>{t.parentsB3Body}</span>
                  </div>
                </div>
              </div>
              <div className="hero-actions" style={{ marginBottom: 0 }}>
                <a className="btn primary" href={practiceUrl}>{t.tryForChild}</a>
                <Link className="btn" to="/login">{t.login}</Link>
                <a className="btn" href="#faq">{t.faqNav}</a>
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="block">
          <div className="wrap">
            <div className="section-head">
              <div className="kicker">{t.faqNav}</div>
              <h2>{t.faqTitle}</h2>
            </div>
            <div className="grid3">
              <article className="card">
                <h3>{t.faq1Title}</h3>
                <p>{t.faq1Body}</p>
              </article>
              <article className="card">
                <h3>{t.faq2Title}</h3>
                <p>{t.faq2Body}</p>
              </article>
              <article className="card">
                <h3>{t.faq3Title}</h3>
                <p>
                  {t.faq3BeforeTry}
                  <strong>{t.tryFree}</strong>
                  {t.faq3BeforeLogin}
                  <strong>{t.login}</strong>
                  {t.faq3After}
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="cta">
          <div className="wrap">
            <div className="cta-box">
              <div>
                <h2>{t.ctaTitle}</h2>
                <p>{t.ctaLead}</p>
              </div>
              <div className="hero-actions" style={{ margin: 0 }}>
                <a className="btn large" href={practiceUrl}>{t.tryNow}</a>
                <Link className="btn large" to="/login">{t.login}</Link>
              </div>
            </div>
          </div>
        </section>

        <div className="teacher-teaser wrap">
          <a className="btn text-link" href="/giao-vien.html">{t.teacherLink}</a>
        </div>
      </main>

      <footer>
        <div className="wrap footer-in">
          <div>
            <Link className="brand" to="/">
              <span className="logo">G</span>
              <span>Giỏi Tiếng Anh</span>
            </Link>
            <div className="sub">{t.footerTagline}</div>
          </div>
          <div className="footer-links">
            <Link to="/login">{t.login}</Link>
            <a href="mailto:hello@gioitienganh.vn">hello@gioitienganh.vn</a>
          </div>
        </div>
      </footer>
    </>
  );
}
